import { html, repeat, unsafeHTML } from "https://cdn.jsdelivr.net/gh/lit/dist@2.7.4/all/lit-all.min.js";
import { WishlistElement } from "https://cdn.jsdelivr.net/npm/@appmate/wishlist@4.29.7/wishlist-element.js";

function slug (str) {
  return str
    .toLowerCase()
    .replaceAll('å', 'a')
    .replaceAll('ø', 'o')
    .replaceAll('æ', 'ae')
    .replaceAll(/\s+/g, '-')
}

class WishlistPage extends WishlistElement {
  static get properties() {
    return {
      moveToCart: { type: Boolean, attribute: "move-to-cart" },
      loginCtaMode: { type: String, attribute: "login-cta-mode" },
      variantAutoSelectMode: {
        type: String,
        attribute: "variant-auto-select-mode",
      },
      showVendor: { type: Boolean, attribute: "show-vendor" },
      showProductTitle: { type: Boolean, attribute: "show-product-title" },
      showShareButton: { type: Boolean, attribute: "show-share-button" },
      showBuyAllButton: { type: Boolean, attribute: "show-buy-all-button" },
      showClearButton: { type: Boolean, attribute: "show-clear-button" },
      ctaButton: { type: String, attribute: "cta-button" },
      productOptions: { type: String, attribute: "product-options" },
      wishliswtEmptyLink: { type: String, attribute: "wishlist-empty-link" },
    };
  }

  getStateConfig() {
    return {
      wishlist: true,
    };
  }

  render() {
    if (!this.wishlist) {
      return;
    }

    return html`
      <section class="wk-page">
        ${this.renderHeader()}
        ${this.renderWishlistItems()}
      </section>
    `;
  }

  renderHeader() {
    return html`
      <div class="flex flex-col lg:flex-row lg:items-end justify-between">
        ${this.renderWishlistEmptyCallout()} ${this.renderLoginCallout()}
        ${this.renderControls()}
      </div>
    `;
  }

  renderControls() {
    if (!this.wishlist.items.length) {
      return;
    }
    if (!this.showShareButton) {
      return;
    }

    return html`
      <div class="wk-controls mt-6 lg:mt-0">
        ${this.showShareButton
      ? html`
              <wishlist-share
                data-wishlist-id="${this.wishlist.id}"
              ></wishlist-share>
            `
      : undefined}
      </div>
    `;
  }

  renderWishlistEmptyCallout() {
    if (this.wishlist.items.length) {
      return;
    }

    return html`
      <div class="rte">
        ${unsafeHTML(theme.strings.wishlist.page.emptyDescription)}
      </div>
    `;
  }

  renderLoginCallout() {
    if (
      this.app.customer ||
      !this.wishlist.isMine ||
      !this.wishlist.items.length
    ) {
      return;
    }
    if (this.loginCtaMode === "DISABLED") {
      return;
    }

    const content = theme.strings.wishlist.page.loginCallout.replace('LOGIN_URL', this.app.routes.accountLoginUrl).replace('REGISTER_URL', this.app.routes.accountRegisterUrl);

    return html`
      <div class="rte lg:w-1/2">
        <p>
          ${unsafeHTML(content)}
        </p>
      </div>
    `;
  }

  renderWishlistItems() {
    if (!this.wishlist.items.length) {
      return;
    }

    const wishlistItems = this.wishlist.items.slice().reverse();

    return html`
      <div class="auto-rows-fr mt-12 lg:mt-9 grid-cols-2 lg:grid-cols-5 grid gap-y-5 gap-x-2.5 lg:gap-5">
        ${repeat(
      wishlistItems,
      (wishlistItem) => wishlistItem.id,
      (wishlistItem) => html`
            <wishlist-product-card
              data-wishlist-id=${this.wishlist.id}
              data-wishlist-item-id=${wishlistItem.id}
              .wishlist=${this.wishlist}
              .moveToCart=${this.moveToCart}
              .showVendor=${this.showVendor}
              .showProductTitle=${this.showProductTitle}
              .ctaButton=${this.ctaButton}
              .productOptions=${this.productOptions}
            ></wishlist-product-card>
          `
    )}
      </div>
    `;
  }

  connectedCallback() {
    if (!this.dataset.wishlistId) {
      this.dataset.wishlistId = this.app.theme.getWishlistId(window.location.pathname);
    }

    if (this.dataset.wishlistId && this.dataset.wishlistId !== "mine") {
      this.loadWithoutSession = true;
    }

    super.connectedCallback();
  }
};

class WishlistProductCard extends WishlistElement {
  static get properties() {
    return {
      wishlist: { type: Object },
      moveToCart: { type: String, attribute: "move-to-cart" },
      showVendor: { type: Boolean, attribute: "show-vendor" },
      showProductTitle: { type: Boolean, attribute: "show-product-title" },
      ctaButton: { type: Boolean, attribute: "cta-button" },
      productOptions: { type: Boolean, attribute: "product-options" },
    };
  }

  constructor() {
    super();
  }

  getStateConfig() {
    return {
      loading: "lazy",
      wishlistItem: {
        productMetafields: [
          { namespace: "custom", key: "labels" },
          { namespace: "data", key: "quantity_steps" },
          { namespace: "data", key: "units" },
        ],
      },
    };
  }

  render() {
    if (!this.wishlistItem.product.id) {
      return html`
        <div class="wk-product-card">${this.renderLoadingState()}</div>
      `;
    }

    if (this.wishlistItem.product.hidden) {
      return html`
        <div class="wk-product-card">${this.renderUnavailableState()}</div>
      `;
    }

    const product = this.wishlistItem.product;

    return html`
      <div class="product-item">
        <div class="group relative overflow-hidden rounded block">
          <a href="${this.getProductUrl(product)}" class="block aspect-h-5 aspect-w-4 bg-gray-200">
            ${this.renderImages({ product })}
          </a>

          <div class="absolute left-2.5 right-2.5 top-2.5 flex-col items-start space-y-1 flex">
            ${this.renderSaleLabel({ product })}
            ${this.renderLabels({ product })}
          </div>

          <div class="absolute top-0 left-0 h-full w-full flex justify-end items-end p-2.5 z-20 pointer-events-none">
            <button
              type="button"
              data-product-handle="${product.handle}"
              class="wishlist-add-button flex items-center justify-center size-9 rounded-full pointer-events-auto">
              <svg class="size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 25">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m12.0009 22.3446-9.58803-10c-.84266-.842-1.39883-1.928-1.589635-3.10382-.190801-1.17586-.006544-2.38195.526635-3.4472.40206-.80388.98937-1.50073 1.71355-2.03312.72418-.53239 1.56449-.88509 2.45171-1.02903.88721-.14395 1.79594-.07502 2.65129.20109.85536.27612 1.63286.75153 2.26848 1.38706l1.566 1.565 1.566-1.565c.6356-.63553 1.4131-1.11094 2.2684-1.38706.8554-.27611 1.7641-.34504 2.6513-.20109.8872.14394 1.7275.49664 2.4517 1.02903.7242.53239 1.3115 1.22924 1.7136 2.03312.5324 1.06484.7164 2.27021.5258 3.4454-.1906 1.17522-.7461 2.26062-1.5878 3.10262l-9.589 10.003Z"/>
              </svg>
            </button>
          </div>
        </div>
        
        <div class="mt-2.5 space-y-0.5 leading-tight">
          ${this.renderProductTitle({ product })}
          
          <p class="space-x-2.5 font-bold leading-tight">
            ${this.renderPrice({ product })}
          </p>

          <button
            type="button"
            data-product-handle="${product.handle}"
            class="wishlist-add-button text-brand-red-950 underline font-bold text-sm">
            ${theme.strings.wishlist.page.removeFromWishlist}
          </button>
        </div>
      </div>
    `;
  }

  renderLabels({product}) {
    if (product.metafields.labels) {
      const labels = JSON.parse(product.metafields.labels);
      return html`
        ${labels.map(label => {
          return html`
          <div class="product-label product-label--${slug(label)}">
          ${label}
        </div>
          `
        })}
      `
    }
  }

  renderSaleLabel({product}) {
    const variant = product.variants[0];

    if (variant.price < variant.compare_at_price) {
      const savings = (variant.compare_at_price - variant.price) * 100 / variant.compare_at_price

      return html`<div class="product-label product-label--on-sale">
${theme.strings.product.discountLabel.replace('%SAVINGS%', Math.round(savings))}%
</div>`
    }
  }

  imageUrl (src) {
    const url = new URL(src);

    url.searchParams.set('crop', 'center');
    url.searchParams.set('height', '{height}');
    url.searchParams.set('width', '{width}');

    return decodeURI(url.toString());
  }

  renderImages({product}) {
    const image = product.images[0];

    if (!image) {
      return ''
    }

    return html`
      <img
        src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
        data-src="${this.imageUrl(image)}"
        data-aspectratio=""
        data-sizes="auto"
        data-focal-x="50"
        data-focal-y="50"
        data-max-width="${1000}"
        data-max-height="${1000}"
        alt="${product.title}"
        class="lazyload"
        decoding="async"
      >

      ${product.images[1] ? html`
      <div
        class="invisible z-10 opacity-0 transition-all duration-300 ease-in-out group-hover:visible group-hover:opacity-100">
        <img
        src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
        data-src="${this.imageUrl(product.images[1])}"
        data-aspectratio=""
        data-sizes="auto"
        data-focal-x="50"
        data-focal-y="50"
        data-max-width="${1000}"
        data-max-height="${1000}"
        alt="${product.title ?? ''}"
        class="lazyload"
        decoding="async"
      >
      </div>
      ` : ''}
    `;
  }

  renderProductTitle({ product }) {
    if (!this.showProductTitle) {
      return;
    }

    let title = '';
    let productType = '';

    let count = 0;
    const spilt = product.title.split('');

    spilt.forEach((character) => {
      if (character === "'") {
        count = count + 1;
      } else {
        if (count > 0) {
          title = title + character;
        } else {
          productType = productType + character;
        }
      }
    });

    if (count < 2) {
      title = product.title
      productType = product.type
    }

    return html`
      <p class="font-medium text-sm">${productType}</p>

      <p class="truncate font-display font-bold lg:text-lg">
        <a href="${this.getProductUrl(product)}">${title}</a>
      </p>
    `;
  }

  renderPrice({ product }) {
    const variant = product.variants[0];

    return html`
        ${this.renderCurrentPrice({ product, variant })}
        ${this.renderComparePrice({ product, variant })}
    `;
  }

  renderCurrentPrice({ product, variant }) {
    if (product.price_min !== product.price_max) {
      return html`
        <span>
          ${theme.strings.product.fromPrice.replace('%PRICE%', this.formatMoney(product.price_min))}
        </span>
      `;
    }

    if ("quantity_steps" in product.metafields && "units" in product.metafields) {
      const units = parseInt(product.metafields.units);
      const quantitySteps = JSON.parse(product.metafields.quantity_steps);

      const split = quantitySteps.at(-1).split(':')

      const unitPrice = (variant.price || product.price_min) / units

      const percentageOff = split.at(-1) / 100;
      const stepUnitPrice = unitPrice * percentageOff

      const price = unitPrice - stepUnitPrice;
      const compareAtPrice = variant.compare_at_price / units;

      return html`
      <span>${theme.strings.product.fromPrice.replace('%PRICE%', this.formatMoney(price))}</span>
      ${compareAtPrice ? `<del>${this.formatMoney(compareAtPrice)}</del>` : ''}
      `
    }

    if (variant) {
      const sale = variant.price < variant.compare_at_price;

      return html`
        <span class="${sale ? "text-brand-sun" : ""}">
          ${this.renderMoney(variant.price)}
        </span>
      `;
    }

    return html`
      <span>
        ${this.renderMoney(product.price_min)}
      </span>
    `;
  }

  renderComparePrice({ variant }) {
    if (variant && variant.price < variant.compare_at_price) {
      return html`
        <del>
          ${this.renderMoney(variant.compare_at_price)}
        </del>
      `;
    }
  }

  renderLoadingState() {
    return html`
      <div class="wk-image">
          <wk-icon icon="spinner" class="wk-loading-spinner"></wk-icon>
        </div>  
      </div>
    `;
  }

  renderUnavailableState() {
    return html`
      <div class="wk-image-link">
        <img
          class="wk-image"
          src=${this.getImageUrl(null, null, { width: 1000, height: 1000 })}
        />
      </div>
      <div class="wk-meta">
        <span class="wk-vendor">&nbsp;</span>
        <span class="wk-product-title">
          ${this.getTranslation("wishlist_page.product_removed_html")}
        </span>
      </div>
      ${this.renderRemoveButton()}
    `;
  }

  renderRemoveButton() {
    if (!this.wishlist.isMine) {
      return;
    }

    return html`
      <remove-button
        data-wishlist-item-id=${this.wishlistItem.id}
        .showIcon=${false}
        .showText=${true}
      ></remove-button>
    `;
  }
};

export class RemoveButton extends WishlistElement {
  static get properties() {
    return {
      floating: { type: Object },
      showIcon: { type: Boolean, attribute: "show-icon" },
      showText: { type: Boolean, attribute: "show-text" },
    };
  }

  getEventConfig() {
    return {
      "click .wk-button": this.handleClick,
    };
  }

  handleClick() {
    return this.app.removeWishlistItem({
      wishlistItemId: this.dataset.wishlistItemId,
    });
  }

  render() {
    const text = this.getTranslation("wishlist_page.remove_product");
    const hint = this.getTranslation("wishlist_page.remove_product");

    return html`
      <wk-button
        .text=${text}
        .hint=${hint}
        .showIcon=${this.showIcon}
        .showText=${this.showText}
        .floating=${this.floating}
        .icon=${"remove"}
      ></wk-button>
    `;
  }
}

export class WishlistShare extends WishlistElement {
  static get properties() {
    return {
      linkCopied: { type: Boolean, state: true },
    };
  }

  getStateConfig() {
    return {
      wishlist: true,
    };
  }

  getEventConfig() {
    return {
      "click [data-wishlist-share]": this.handleClick,
    };
  }

  async handleClick() {
    const { clipboard } = await this.app.shareWishlist({
      wishlistId: this.wishlist.publicId,
      title: window.theme.strings.wishlist.share.title,
      text: window.theme.strings.wishlist.share.message,
    });

    if (clipboard) {
      this.linkCopied = true;
      await new Promise((resolve) => setTimeout(resolve, 2000));
      this.linkCopied = false;
    }
  }

  render() {
    const text = this.linkCopied
      ? window.theme.strings.wishlist.share.linkCopied
      : window.theme.strings.wishlist.share.buttonLabel;

    return html`
      <button type="button" data-wishlist-share class="button button--lg button--dark">
        ${text}
      </button>
    `;
  }
}

customElements.define("wishlist-page", WishlistPage);
customElements.define("wishlist-product-card", WishlistProductCard);
customElements.define("remove-button", RemoveButton);
customElements.define("wishlist-share", WishlistShare);
